// src/services/DocumentService.js
import { storage } from '../firebase';
import { ref, listAll, getDownloadURL, getMetadata } from 'firebase/storage';
import { openDB } from 'idb';

// Debug logger function to standardize logging
const debug = {
  log: (message, data) => {
    console.log(`[DocumentService] ${message}`, data !== undefined ? data : '');
  },
  error: (message, error) => {
    console.error(`[DocumentService] ${message}`, error);
    if (error && error.stack) {
      console.error(`[DocumentService] Error Stack: ${error.stack}`);
    }
  },
  warn: (message, data) => {
    console.warn(`[DocumentService] ${message}`, data !== undefined ? data : '');
  }
};

// IndexedDB setup
const DB_NAME = 'trinityHarperDocs';
const DB_VERSION = 1;
const DOCS_STORE = 'documents';
const CONTENT_STORE = 'documentContents';
const SYNC_STORE = 'syncStatus';
const QUEUE_STORE = 'pendingDocuments';

// Initialize the database
const initDB = async () => {
  debug.log('Initializing IndexedDB');
  try {
    const db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(DOCS_STORE)) {
          debug.log('Creating documents store');
          const docStore = db.createObjectStore(DOCS_STORE, { keyPath: 'id' });
          docStore.createIndex('section', 'section', { unique: false });
        }
        if (!db.objectStoreNames.contains(CONTENT_STORE)) {
          debug.log('Creating document contents store');
          db.createObjectStore(CONTENT_STORE, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(SYNC_STORE)) {
          debug.log('Creating sync status store');
          db.createObjectStore(SYNC_STORE, { keyPath: 'section' });
        }
        if (!db.objectStoreNames.contains(QUEUE_STORE)) {
          debug.log('Creating pending documents queue store');
          db.createObjectStore(QUEUE_STORE, { keyPath: 'id' });
        }
      }
    });
    debug.log('IndexedDB initialized successfully');
    return db;
  } catch (error) {
    debug.error('Failed to initialize IndexedDB', error);
    throw error;
  }
};

/**
 * Properly convert a document ID back to its original Firebase Storage path.
 * @param {string} documentId - The document ID (with underscores)
 * @returns {string} The original Firebase Storage path
 */
const getPathFromDocumentId = (documentId) => {
  if (!documentId) return null;
  
  // The pattern we use to create IDs is replacing / and . with _
  let path = documentId.replace('Firebase_upload_', 'Firebase_upload/');
  
  const majorSections = [
    'Master Vehicle File',
    'Health & Safety Policy Manual',
    'Insurance Certificates',
    'COSHH',
    'Risk Assessments',
    'Safe Systems of Work',
    'Employee Handbook'
  ];
  
  majorSections.forEach(section => {
    const sectionWithUnderscores = section.replace(/\s+/g, '_');
    const regex = new RegExp(`_${sectionWithUnderscores}`, 'g');
    path = path.replace(regex, `/${section}`);
  });
  
  path = path.replace(/\/(\d+)_\s+/g, '/$1. ');
  
  // Handle file extensions (.docx, .pdf, etc.)
  path = path.replace(/_([a-zA-Z0-9]{1,5})$/, '.$1');
  
  debug.log(`Converted document ID ${documentId} to path: ${path}`);
  return path;
};

/**
 * More reliable network connectivity check
 * @returns {Promise<boolean>} True if actually online
 */
const checkRealNetworkConnectivity = async () => {
  if (!navigator.onLine) {
    return false;
  }
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);
    await fetch('https://1.1.1.1', {
      method: 'HEAD', 
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return true;
  } catch (err) {
    console.log('[Network Check] Device reports online but network request failed:', err);
    return false;
  }
};

// Get document metadata from Firebase Storage
const fetchDocumentsFromStorage = async (sectionPath) => {
  debug.log(`Fetching documents from path: ${sectionPath}`);
  
  try {
    const folderRef = ref(storage, sectionPath);
    debug.log('Created folder reference', folderRef);
    debug.log('Listing all items in folder');
    const listResult = await listAll(folderRef);
    debug.log(`Found ${listResult.items.length} items and ${listResult.prefixes.length} prefixes in ${sectionPath}`);
    
    const documents = await Promise.all(
      listResult.items.map(async (itemRef) => {
        try {
          debug.log(`Processing item: ${itemRef.name}, full path: ${itemRef.fullPath}`);
          const metadata = await getMetadata(itemRef);
          debug.log(`Got metadata for ${itemRef.name}`, metadata);
          const url = await getDownloadURL(itemRef);
          debug.log(`Got download URL for ${itemRef.name}`);
          const id = itemRef.fullPath.replace(/[\/\.]/g, '_');
          
          return {
            id: id,
            name: itemRef.name,
            path: itemRef.fullPath,
            url: url,
            size: metadata.size,
            contentType: metadata.contentType,
            updated: metadata.updated,
            timeCreated: metadata.timeCreated,
            section: sectionPath
          };
        } catch (error) {
          debug.error(`Error processing item ${itemRef.name}`, error);
          return null;
        }
      })
    );
    
    const validDocuments = documents.filter(doc => doc !== null);
    debug.log(`Successfully processed ${validDocuments.length} out of ${listResult.items.length} items`);
    return validDocuments;
  } catch (error) {
    debug.error(`Error fetching documents from ${sectionPath}`, error);
    throw error;
  }
};

// Save documents to IndexedDB
const saveDocumentsToIndexedDB = async (documents, sectionPath) => {
  debug.log(`Saving ${documents.length} documents to IndexedDB for section: ${sectionPath}`);
  try {
    const db = await initDB();
    const tx = db.transaction(DOCS_STORE, 'readwrite');
    const store = tx.objectStore(DOCS_STORE);
    for (const doc of documents) {
      await store.put(doc);
      debug.log(`Saved document ${doc.name} to IndexedDB`);
    }
    const syncTx = db.transaction(SYNC_STORE, 'readwrite');
    const syncStore = syncTx.objectStore(SYNC_STORE);
    await syncStore.put({
      section: sectionPath,
      lastSync: new Date().toISOString()
    });
    await tx.done;
    await syncTx.done;
    debug.log(`Successfully saved all documents from ${sectionPath} to IndexedDB`);
    return documents;
  } catch (error) {
    debug.error('Error saving documents to IndexedDB', error);
    throw error;
  }
};

// Fetch document content and cache it
const cacheDocumentContent = async (documentId, documentPath) => {
  debug.log(`Caching content for document ID: ${documentId}, path: ${documentPath}`);
  try {
    const db = await initDB();
    const fileRef = ref(storage, documentPath);
    debug.log('Created file reference', fileRef);
    const url = await getDownloadURL(fileRef);
    debug.log('Got download URL for document');
    debug.log('Fetching document content');
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch document: ${response.statusText}`);
    }
    const blob = await response.blob();
    debug.log(`Fetched document content, size: ${blob.size} bytes`);
    
    const tx = db.transaction(CONTENT_STORE, 'readwrite');
    const store = tx.objectStore(CONTENT_STORE);
    await store.put({
      id: documentId,
      content: blob,
      lastCached: new Date().toISOString()
    });
    
    const docTx = db.transaction(DOCS_STORE, 'readwrite');
    const docStore = docTx.objectStore(DOCS_STORE);
    const doc = await docStore.get(documentId);
    if (doc) {
      doc.lastCached = new Date().toISOString();
      await docStore.put(doc);
      debug.log(`Updated document metadata for ${documentId}`);
    }
    
    await tx.done;
    await docTx.done;
    debug.log(`Successfully cached content for document: ${documentPath}`);
    return blob;
  } catch (error) {
    debug.error('Error caching document content', error);
    throw error;
  }
};

/**
 * Store a blob directly in IndexedDB for a document.
 * @param {string} documentId - The document ID
 * @param {Blob} blob - The blob content to store
 * @returns {Promise<boolean>}
 */
const storeBlobInIndexedDB = async (documentId, blob) => {
  try {
    const db = await initDB();
    const tx = db.transaction(CONTENT_STORE, 'readwrite');
    const store = tx.objectStore(CONTENT_STORE);
    await store.put({
      id: documentId,
      content: blob,
      lastCached: new Date().toISOString()
    });
    await tx.done;
    debug.log(`Successfully stored blob for document: ${documentId}`);
    return true;
  } catch (error) {
    debug.error('Error storing blob in IndexedDB', error);
    throw error;
  }
};

/**
 * Update a document's cache status.
 * @param {string} documentId - The document ID
 * @returns {Promise<boolean>}
 */
const updateDocumentCacheStatus = async (documentId) => {
  try {
    const db = await initDB();
    const docTx = db.transaction(DOCS_STORE, 'readwrite');
    const docStore = docTx.objectStore(DOCS_STORE);
    const doc = await docStore.get(documentId);
    if (doc) {
      doc.lastCached = new Date().toISOString();
      await docStore.put(doc);
      debug.log(`Updated document cache status for ${documentId}`);
    }
    await docTx.done;
    return true;
  } catch (error) {
    debug.error('Error updating document cache status', error);
    throw error;
  }
};

/**
 * Remove a document from the cache.
 * @param {string} documentId - The document ID to remove
 * @returns {Promise<boolean>}
 */
const removeDocumentFromCache = async (documentId) => {
  try {
    const db = await initDB();
    const contentTx = db.transaction(CONTENT_STORE, 'readwrite');
    const contentStore = contentTx.objectStore(CONTENT_STORE);
    await contentStore.delete(documentId);
    
    const docTx = db.transaction(DOCS_STORE, 'readwrite');
    const docStore = docTx.objectStore(DOCS_STORE);
    const doc = await docStore.get(documentId);
    if (doc) {
      delete doc.lastCached;
      await docStore.put(doc);
    }
    
    await contentTx.done;
    await docTx.done;
    debug.log(`Removed document ${documentId} from cache`);
    return true;
  } catch (error) {
    debug.error(`Error removing document ${documentId} from cache`, error);
    return false;
  }
};

/**
 * Retrieve document metadata from IndexedDB.
 * @param {string} documentId - The document ID
 * @returns {Promise<Object|null>} - Document metadata or null if not found
 */
const getDocumentMetadata = async (documentId) => {
  debug.log(`Getting metadata for document ID: ${documentId}`);
  try {
    const db = await initDB();
    const tx = db.transaction(DOCS_STORE, 'readonly');
    const store = tx.objectStore(DOCS_STORE);
    const doc = await store.get(documentId);
    
    if (doc) {
      debug.log(`Found document metadata in IndexedDB: ${documentId}`);
      return doc;
    }
    
    // If not found in IndexedDB, try to reconstruct the path.
    const reconstructedPath = getPathFromDocumentId(documentId);
    debug.log(`Reconstructed path for ${documentId}: ${reconstructedPath}`);
    
    if (reconstructedPath) {
      return {
        id: documentId,
        path: reconstructedPath,
        name: reconstructedPath.split('/').pop() || documentId
      };
    }
    
    debug.warn(`Could not get metadata for document: ${documentId}`);
    return null;
  } catch (error) {
    debug.error(`Error getting document metadata: ${documentId}`, error);
    throw error;
  }
};

/**
 * Check if a document is already cached in IndexedDB.
 * @param {string} documentId - The document ID to check
 * @returns {Promise<boolean>} - True if document is cached
 */
const isDocumentCached = async (documentId) => {
  debug.log(`Checking if document is cached: ${documentId}`);
  try {
    const db = await initDB();
    const contentTx = db.transaction(CONTENT_STORE, 'readonly');
    const contentStore = contentTx.objectStore(CONTENT_STORE);
    const cachedContent = await contentStore.get(documentId);
    if (cachedContent && cachedContent.content) {
      debug.log(`Document is cached: ${documentId}`);
      return true;
    }
    const docTx = db.transaction(DOCS_STORE, 'readonly');
    const docStore = docTx.objectStore(DOCS_STORE);
    const doc = await docStore.get(documentId);
    if (doc && doc.lastCached) {
      debug.warn(`Document metadata indicates cached but content not found: ${documentId}`);
      return false;
    }
    debug.log(`Document is not cached: ${documentId}`);
    return false;
  } catch (error) {
    debug.error(`Error checking if document is cached: ${documentId}`, error);
    return false;
  }
};

/**
 * Add a document to the pending download queue.
 * @param {string} documentId
 * @returns {Promise<boolean>}
 */
const addToDownloadQueue = async (documentId) => {
  const db = await initDB();
  const tx = db.transaction(QUEUE_STORE, 'readwrite');
  const store = tx.objectStore(QUEUE_STORE);
  await store.put({
    id: documentId,
    queued: new Date().toISOString(),
    attempts: 0
  });
  debug.log(`Added document to download queue: ${documentId}`);
  return true;
};

const DocumentService = {
  testFirebaseStorage: async () => {
    debug.log('Testing Firebase Storage connection');
    try {
      const rootRef = ref(storage);
      debug.log('Created root storage reference', rootRef);
      const listResult = await listAll(rootRef);
      debug.log('Successfully listed root storage folder', {
        prefixes: listResult.prefixes.map(p => p.fullPath),
        items: listResult.items.map(i => i.fullPath)
      });
      return {
        success: true,
        prefixes: listResult.prefixes.map(p => p.fullPath),
        items: listResult.items.map(i => i.fullPath)
      };
    } catch (error) {
      debug.error('Error testing Firebase Storage connection', error);
      return {
        success: false,
        error: error.message,
        code: error.code
      };
    }
  },
  
  validatePath: async (path) => {
    debug.log(`Validating path: ${path}`);
    try {
      const folderRef = ref(storage, path);
      const listResult = await listAll(folderRef);
      debug.log(`Path validated. Contains ${listResult.prefixes.length} folders and ${listResult.items.length} files`);
      return {
        valid: true,
        prefixes: listResult.prefixes.length,
        items: listResult.items.length
      };
    } catch (error) {
      debug.error(`Path validation failed for: ${path}`, error);
      return {
        valid: false,
        error: error.message,
        code: error.code
      };
    }
  },
  
  fetchDocumentsBySection: async (sectionPath) => {
    console.log(`[OFFLINE TEST] Current online status: ${navigator.onLine}`);
    console.log(`[OFFLINE TEST] Fetching section: ${sectionPath}`);
    debug.log(`Fetching documents by section: ${sectionPath}`);
    try {
      let documents = [];
      let actuallyOnline = navigator.onLine;
      
      if (actuallyOnline) {
        try {
          actuallyOnline = await checkRealNetworkConnectivity();
          console.log(`[OFFLINE TEST] Enhanced network check result: ${actuallyOnline ? 'online' : 'offline'}`);
        } catch (networkError) {
          console.log(`[OFFLINE TEST] Network check failed, treating as offline`);
          actuallyOnline = false;
        }
      }
      
      if (actuallyOnline) {
        try {
          const pathValidation = await DocumentService.validatePath(sectionPath);
          if (!pathValidation.valid) {
            debug.error(`Invalid section path: ${sectionPath}`, pathValidation.error);
            throw new Error(`Invalid section path: ${sectionPath} - ${pathValidation.error}`);
          }
          debug.log('Device is online, fetching from Firebase Storage');
          documents = await fetchDocumentsFromStorage(sectionPath);
          debug.log(`Fetched ${documents.length} documents from Firebase Storage`);
          if (documents.length > 0) {
            await saveDocumentsToIndexedDB(documents, sectionPath);
          } else {
            debug.warn(`No documents found in section: ${sectionPath}`);
          }
        } catch (onlineError) {
          console.log(`[OFFLINE TEST] Online fetch failed with error: `, onlineError);
          actuallyOnline = false;
        }
      }
      
      if (!actuallyOnline) {
        console.log(`[OFFLINE TEST] Using offline mode to fetch documents`);
        debug.log('Device is offline, fetching from IndexedDB');
        try {
          const db = await initDB();
          console.log(`[OFFLINE TEST] DB initialized for offline fetch`);
          try {
            const tx = db.transaction(DOCS_STORE, 'readonly');
            const store = tx.objectStore(DOCS_STORE);
            if (store.indexNames.contains('section')) {
              console.log(`[OFFLINE TEST] Using section index to find documents`);
              const index = store.index('section');
              documents = await index.getAll(sectionPath);
              console.log(`[OFFLINE TEST] Found ${documents.length} documents by index`);
            } else {
              console.log(`[OFFLINE TEST] No section index, falling back to manual filtering`);
              const allDocs = await store.getAll();
              documents = allDocs.filter(doc => doc.section === sectionPath);
              console.log(`[OFFLINE TEST] Found ${documents.length} documents by filtering`);
            }
          } catch (indexError) {
            console.log(`[OFFLINE TEST] Error using index: `, indexError);
            const tx = db.transaction(DOCS_STORE, 'readonly');
            const store = tx.objectStore(DOCS_STORE);
            const allDocs = await store.getAll();
            console.log(`[OFFLINE TEST] Got ${allDocs.length} total documents from DB`);
            documents = allDocs.filter(doc => doc.section === sectionPath);
            console.log(`[OFFLINE TEST] Filtered to ${documents.length} documents for section`);
          }
        } catch (offlineError) {
          console.error(`[OFFLINE TEST] Offline retrieval failed: `, offlineError);
          documents = [];
        }
      }
      
      return documents;
    } catch (error) {
      debug.error('Error in fetchDocumentsBySection', error);
      console.log(`[OFFLINE TEST] Main error handler triggered:`, error);
      try {
        console.log(`[OFFLINE TEST] Attempting emergency fallback`);
        const db = await initDB();
        const store = db.transaction(DOCS_STORE, 'readonly').objectStore(DOCS_STORE);
        const allDocs = await store.getAll();
        console.log(`[OFFLINE TEST] Emergency fallback found ${allDocs.length} total documents`);
        const matchingDocs = allDocs.filter(doc => doc.section === sectionPath);
        console.log(`[OFFLINE TEST] Emergency found ${matchingDocs.length} documents for section ${sectionPath}`);
        return matchingDocs;
      } catch (emergencyError) {
        console.error(`[OFFLINE TEST] All fallbacks failed:`, emergencyError);
        return [];
      }
    }
  },
  
  getStorageDebugInfo: async () => {
    debug.log('Getting storage debug info');
    try {
      const info = {
        firebase: {},
        indexedDB: {},
        navigator: {
          onLine: navigator.onLine,
          userAgent: navigator.userAgent
        }
      };
      const firebaseTest = await DocumentService.testFirebaseStorage();
      info.firebase = firebaseTest;
      try {
        const db = await initDB();
        const docsTx = db.transaction(DOCS_STORE, 'readonly');
        const docsStore = docsTx.objectStore(DOCS_STORE);
        const allDocs = await docsStore.getAll();
        const syncTx = db.transaction(SYNC_STORE, 'readonly');
        const syncStore = syncTx.objectStore(SYNC_STORE);
        const allSyncs = await syncStore.getAll();
        info.indexedDB = {
          status: 'connected',
          documentCount: allDocs.length,
          syncedSections: allSyncs.length,
          sections: allSyncs.map(s => s.section)
        };
      } catch (dbError) {
        info.indexedDB = {
          status: 'error',
          error: dbError.message
        };
      }
      debug.log('Storage debug info collected', info);
      return info;
    } catch (error) {
      debug.error('Error collecting storage debug info', error);
      return {
        error: error.message,
        stack: error.stack
      };
    }
  },
  
  getDocumentContent: async (documentId) => {
    debug.log(`Getting content for document ID: ${documentId}`);
    try {
      const db = await initDB();
      const contentTx = db.transaction(CONTENT_STORE, 'readonly');
      const contentStore = contentTx.objectStore(CONTENT_STORE);
      const cachedContent = await contentStore.get(documentId);
      if (cachedContent && cachedContent.content) {
        debug.log('Using cached content');
        return cachedContent.content;
      }
      
      const isOnline = await checkRealNetworkConnectivity();
      if (isOnline) {
        let documentPath;
        const docMetadata = await getDocumentMetadata(documentId);
        if (docMetadata && docMetadata.path) {
          documentPath = docMetadata.path;
        } else {
          documentPath = getPathFromDocumentId(documentId);
        }
        if (documentPath) {
          debug.log(`Fetching and caching content from: ${documentPath}`);
          return await cacheDocumentContent(documentId, documentPath);
        } else {
          debug.error('Document path could not be determined', { documentId });
          throw new Error('Document path could not be determined');
        }
      } else {
        debug.error('Document not available offline', { documentId });
        throw new Error('Document not available offline');
      }
    } catch (error) {
      debug.error('Error getting document content', error);
      throw error;
    }
  },
  
  getLastSyncStatus: async (sectionPath) => {
    debug.log(`Getting last sync status for section: ${sectionPath}`);
    try {
      const db = await initDB();
      const tx = db.transaction(SYNC_STORE, 'readonly');
      const store = tx.objectStore(SYNC_STORE);
      const syncStatus = await store.get(sectionPath);
      if (syncStatus) {
        debug.log(`Last sync for ${sectionPath}: ${syncStatus.lastSync}`);
      } else {
        debug.log(`No sync status found for ${sectionPath}`);
      }
      return syncStatus ? syncStatus.lastSync : null;
    } catch (error) {
      debug.error('Error getting last sync status', error);
      return null;
    }
  },
  
  syncWithServer: async () => {
    debug.log('Syncing with server');
    if (!navigator.onLine) {
      debug.warn('Cannot sync while offline');
      return false;
    }
    const actuallyOnline = await checkRealNetworkConnectivity();
    if (!actuallyOnline) {
      debug.warn('Network check failed, cannot sync');
      return false;
    }
    try {
      const db = await initDB();
      const syncTx = db.transaction(SYNC_STORE, 'readonly');
      const syncStore = syncTx.objectStore(SYNC_STORE);
      const allSyncStatus = await syncStore.getAll();
      debug.log(`Syncing ${allSyncStatus.length} sections with the server`);
      const results = [];
      for (const status of allSyncStatus) {
        debug.log(`Syncing section: ${status.section}`);
        try {
          const docs = await DocumentService.fetchDocumentsBySection(status.section);
          results.push({
            section: status.section,
            success: true,
            documentCount: docs.length
          });
        } catch (sectionError) {
          debug.error(`Error syncing section: ${status.section}`, sectionError);
          results.push({
            section: status.section,
            success: false,
            error: sectionError.message
          });
        }
      }
      return {
        success: true,
        results: results
      };
    } catch (error) {
      debug.error('Error syncing with server', error);
      return {
        success: false,
        error: error.message
      };
    }
  },
  
  /**
   * Cache a specific document for offline use.
   * If offline, the document is added to the download queue and background sync is registered.
   */
  cacheDocumentForOffline: async (documentId) => {
    debug.log(`Caching document for offline use: ${documentId}`);
    
    // If the browser reports offline, queue the document and try to register background sync.
    if (!navigator.onLine) {
      debug.log('Device is offline, registering background sync for document');
      try {
        await addToDownloadQueue(documentId);
        if ('serviceWorker' in navigator && 'SyncManager' in window) {
          const registration = await navigator.serviceWorker.ready;
          await registration.sync.register('document-sync');
          debug.log('Background sync registered for document');
        }
        return {
          success: false,
          queued: true,
          reason: 'offline',
          message: 'Document queued for download when online'
        };
      } catch (syncError) {
        debug.error('Error setting up background sync', syncError);
        return {
          success: false,
          queued: true,
          reason: 'offline',
          message: 'Document queued but sync registration failed'
        };
      }
    }
    
    try {
      const isCached = await isDocumentCached(documentId);
      if (isCached) {
        debug.log(`Document is already cached: ${documentId}`);
        return { success: true };
      }
      
      const isOnline = await checkRealNetworkConnectivity();
      if (!isOnline) {
        debug.error(`Cannot cache document while offline: ${documentId}`);
        throw new Error('Cannot cache document while offline');
      }
      
      let docPath;
      const metadata = await getDocumentMetadata(documentId);
      if (metadata && metadata.path) {
        docPath = metadata.path;
      } else {
        docPath = getPathFromDocumentId(documentId);
      }
      
      if (!docPath) {
        debug.warn(`Could not determine path for document: ${documentId}`);
        return { success: false, reason: 'path', message: 'Document path not determined' };
      }
      
      debug.log(`Caching document content from path: ${docPath}`);
      await cacheDocumentContent(documentId, docPath);
      return { success: true };
    } catch (error) {
      debug.error('Error caching document for offline use', error);
      if (error.message.includes('offline')) {
        return {
          success: false,
          reason: 'offline',
          message: 'Cannot cache document while offline'
        };
      }
      return {
        success: false,
        reason: 'unknown',
        message: error.message
      };
    }
  },
  
  // Queue a document for download.
  // If online, attempts immediate caching; otherwise adds it to the download queue.
  queueDocumentForDownload: async (documentId) => {
    if (navigator.onLine) {
      return await DocumentService.cacheDocumentForOffline(documentId);
    } else {
      await addToDownloadQueue(documentId);
      return {
        success: false,
        queued: true,
        reason: 'offline',
        message: 'Document queued for download when online'
      };
    }
  },
  
  // Process the download queue when connectivity is restored.
  processDownloadQueue: async () => {
    debug.log('Processing download queue');
    try {
      const db = await initDB();
      const tx = db.transaction(QUEUE_STORE, 'readonly');
      const store = tx.objectStore(QUEUE_STORE);
      const queue = await store.getAll();
      debug.log(`Found ${queue.length} documents in download queue`);
      const results = [];
      for (const item of queue) {
        try {
          const res = await DocumentService.cacheDocumentForOffline(item.id);
          if (res.success) {
            const deleteTx = db.transaction(QUEUE_STORE, 'readwrite');
            const deleteStore = deleteTx.objectStore(QUEUE_STORE);
            await deleteStore.delete(item.id);
            await deleteTx.done;
            results.push({ id: item.id, success: true });
          } else {
            const updateTx = db.transaction(QUEUE_STORE, 'readwrite');
            const updateStore = updateTx.objectStore(QUEUE_STORE);
            item.attempts++;
            await updateStore.put(item);
            await updateTx.done;
            results.push({ id: item.id, success: false });
          }
        } catch (error) {
          results.push({ id: item.id, success: false, error: error.message });
        }
      }
      return {
        processed: queue.length,
        results: results
      };
    } catch (error) {
      debug.error('Error processing download queue', error);
      return {
        processed: 0,
        results: [],
        error: error.message
      };
    }
  },
  
  getOfflineDocuments: async () => {
    debug.log('Getting offline documents');
    try {
      const db = await initDB();
      const tx = db.transaction(DOCS_STORE, 'readonly');
      const store = tx.objectStore(DOCS_STORE);
      const allDocs = await store.getAll();
      const offlineDocs = allDocs.filter(doc => doc.lastCached !== undefined);
      debug.log(`Found ${offlineDocs.length} offline documents out of ${allDocs.length} total`);
      return offlineDocs;
    } catch (error) {
      debug.error('Error getting offline documents', error);
      return [];
    }
  },
  
  /**
   * Clear all cached data.
   * Only clears caches if explicitly requested by the user (userInitiated = true).
   * This prevents accidental cache clearance during app updates.
   */
  clearAllCachedData: async (userInitiated = false) => {
    debug.log('Attempting to clear all cached data');
    
    // Only clear if explicitly requested by the user, not during app updates.
    if (!userInitiated) {
      debug.warn('Clear cache operation aborted because it was not user initiated.');
      return false;
    }
    
    try {
      const db = await initDB();
      const docsTx = db.transaction(DOCS_STORE, 'readwrite');
      await docsTx.objectStore(DOCS_STORE).clear();
      const contentTx = db.transaction(CONTENT_STORE, 'readwrite');
      await contentTx.objectStore(CONTENT_STORE).clear();
      const syncTx = db.transaction(SYNC_STORE, 'readwrite');
      await syncTx.objectStore(SYNC_STORE).clear();
      await docsTx.done;
      await contentTx.done;
      await syncTx.done;
      debug.log('All cached data cleared successfully');
      return true;
    } catch (error) {
      debug.error('Error clearing cached data', error);
      return false;
    }
  },
  
  cacheEntireSectionRecursively: async (sectionPath) => {
    debug.log(`Caching entire section recursively: ${sectionPath}`);
    try {
      const isOnline = await checkRealNetworkConnectivity();
      if (!isOnline) {
        debug.error(`Cannot cache section while offline: ${sectionPath}`);
        throw new Error('Cannot cache section while offline');
      }
      const documents = await fetchDocumentsFromStorage(sectionPath);
      if (documents.length > 0) {
        await saveDocumentsToIndexedDB(documents, sectionPath);
        for (const doc of documents) {
          try {
            await cacheDocumentContent(doc.id, doc.path);
            debug.log(`Successfully cached document: ${doc.name}`);
          } catch (docError) {
            debug.error(`Error caching document: ${doc.name}`, docError);
          }
        }
      }
      const folderRef = ref(storage, sectionPath);
      const result = await listAll(folderRef);
      for (const subfolder of result.prefixes) {
        await DocumentService.cacheEntireSectionRecursively(subfolder.fullPath);
      }
      return true;
    } catch (error) {
      debug.error(`Error caching section recursively: ${sectionPath}`, error);
      throw error;
    }
  },
  
  getCacheSize: async () => {
    debug.log('Getting total cache size');
    try {
      const db = await initDB();
      const contentTx = db.transaction(CONTENT_STORE, 'readonly');
      const contentStore = contentTx.objectStore(CONTENT_STORE);
      const allCachedContent = await contentStore.getAll();
      let totalSize = 0;
      for (const item of allCachedContent) {
        if (item.content && item.content.size) {
          totalSize += item.content.size;
        }
      }
      return {
        documentCount: allCachedContent.length,
        totalSizeBytes: totalSize,
        totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2)
      };
    } catch (error) {
      debug.error('Error getting cache size', error);
      return {
        documentCount: 0,
        totalSizeBytes: 0,
        totalSizeMB: 0,
        error: error.message
      };
    }
  },
  
  // Directly expose helper functions.
  storeBlobInIndexedDB,
  updateDocumentCacheStatus,
  removeDocumentFromCache,
  isDocumentCached,
  getDocumentMetadata,
  getPathFromDocumentId,
  
  verifyStorageIntegrity: async () => {
    debug.log('Verifying storage integrity');
    try {
      const db = await initDB();
      const docsTx = db.transaction(DOCS_STORE, 'readwrite');
      const docsStore = docsTx.objectStore(DOCS_STORE);
      const allDocs = await docsStore.getAll();
      const contentTx = db.transaction(CONTENT_STORE, 'readonly');
      const contentStore = contentTx.objectStore(CONTENT_STORE);
      let fixedCount = 0;
      for (const doc of allDocs) {
        if (doc.lastCached) {
          const content = await contentStore.get(doc.id);
          if (!content || !content.content) {
            debug.log(`Fixing inconsistent cache status for ${doc.id}`);
            delete doc.lastCached;
            await docsStore.put(doc);
            fixedCount++;
          }
        }
      }
      await docsTx.done;
      return {
        success: true,
        checked: allDocs.length,
        fixed: fixedCount
      };
    } catch (error) {
      debug.error('Error verifying storage integrity', error);
      return {
        success: false,
        error: error.message
      };
    }
  },
  
  initializeDatabase: async () => {
    try {
      const db = await initDB();
      return {
        success: true,
        message: 'Database initialized successfully'
      };
    } catch (error) {
      debug.error('Error initializing database', error);
      return {
        success: false,
        error: error.message
      };
    }
  },
  
  checkDocumentAvailability: async (documentId) => {
    try {
      const isCached = await isDocumentCached(documentId);
      let metadata = null;
      try {
        metadata = await getDocumentMetadata(documentId);
      } catch (metadataError) {
        debug.warn(`Could not get metadata for ${documentId}`, metadataError);
      }
      const isOnline = await checkRealNetworkConnectivity();
      return {
        documentId,
        isCached,
        hasMetadata: !!metadata,
        metadata: metadata,
        isOnline,
        isAvailable: isCached || isOnline,
        offlineOnly: isCached && !isOnline
      };
    } catch (error) {
      debug.error(`Error checking document availability: ${documentId}`, error);
      return {
        documentId,
        error: error.message,
        isCached: false,
        isAvailable: false
      };
    }
  }
};

export default DocumentService;
